import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';

import {
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  onSnapshot,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatter for DD MMM YYYY (e.g., 07 Oct 2025)
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Date-time formatter for header (DD MMM YYYY, HH:MM AM/PM)
const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Date formatter for filename (YYYY-MM-DD)
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Natural sort function for alphanumeric codes (e.g., 10-001 < 10-002 < 20-001)
const naturalSort = (a, b) => {
  const codeA = String(a.code ?? '').trim();
  const codeB = String(b.code ?? '').trim();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const HotelAccounts = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newAccount, setNewAccount] = useState({ 
    name: '',
    type: 'Asset',
    userId: user?.uid || '',
    code: '',
    financialStatement: '',
    active: true
  });
  const [editingAccount, setEditingAccount] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(true);
  const [hasFetchedAccounts, setHasFetchedAccounts] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const accountsCollectionRef = collection(db, "hotelAccounts");
  const hotelName = `${HOTEL_NAME}`;

  useEffect(() => {
    if (!user?.uid || !userData) return;

    setIsFetchingAccounts(true);

    const q = query(accountsCollectionRef);  // same query as before (no filter)

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const accountList = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id
        }));
        setAccounts(accountList);
        setHasFetchedAccounts(true);
        setIsFetchingAccounts(false);
      },
      (err) => {
        console.error("Error in accounts listener:", err);
        setError("Failed to load accounts");
        setShowModal(true);
        setIsFetchingAccounts(false);
      }
    );

    // Important: cleanup when component unmounts / user changes
    return () => unsubscribe();
  }, [user?.uid, userData]);   // dependencies – safe & minimal

  useEffect(() => {
    const filtered = accounts.filter((account) =>
      account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (account.code && account.code.includes(searchTerm))
    );
    setFilteredAccounts(filtered);
  }, [searchTerm, accounts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAccount({ ...newAccount, [name]: value });
  };

  const handleAddAccount = async () => {
    if (!newAccount.name.trim()) {
      setError("Account name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(accountsCollectionRef, {
        ...newAccount,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Account added successfully");
      // fetchAccounts();
      setNewAccount({ 
        name: '',
        type: 'Asset',
        userId: user?.uid || '',
        code: '',
        financialStatement: '',
        active: true
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add account");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateAccount = async () => {
    setUpdate(true);
    try {
      const accountDoc = doc(db, "hotelAccounts", editingAccount.id);
      await updateDoc(accountDoc, newAccount);
      setSuccessMessage("Account updated successfully");
      // fetchAccounts();
      setEditingAccount(null);
      setNewAccount({ 
        name: '',
        type: 'Asset',
        userId: user?.uid || '',
        code: '',
        financialStatement: '',
        active: true
      });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update account");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  
  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setError("");
    setSuccessMessage("");

    try {
      let accountsData = [];
      let headers = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Get all data with headers
        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,          // first row = headers
          defval: "",         // empty cells = ""
          blankrows: false
        });

        if (json.length < 1) throw new Error("File is empty");

        headers = json[0].map(h => (h || '').toString().trim().toLowerCase());
        accountsData = json.slice(1); // skip header row
      } else {
        // CSV with PapaParse
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.trim().toLowerCase()
        });

        if (result.errors.length > 0) {
          throw new Error("CSV parsing error: " + result.errors[0].message);
        }

        headers = Object.keys(result.data[0] || {}).map(h => h.toLowerCase());
        accountsData = result.data;
      }

      if (accountsData.length === 0) {
        throw new Error("No data rows found after header");
      }

      // ── Column name mapping (case-insensitive) ────────────────────────
      const colMap = {
        name: headers.find(h => ['name', 'accountname', 'title'].includes(h)),
        type: headers.find(h => ['type', 'accounttype', 'category'].includes(h)),
        code: headers.find(h => ['code', 'accountcode', 'number', 'id'].includes(h)),
        financialstatement: headers.find(h => 
          ['financialstatement', 'statement', 'financial statement', 'fs'].includes(h)
        ),
        status: headers.find(h => ['active'].includes(h))
      };

      console.log("Detected columns:", colMap); // ← helpful for debugging

      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < accountsData.length; i++) {
        const row = accountsData[i];
        
        // Get values using mapped column names (Excel uses array, CSV uses object)
        const getValue = (key) => {
          if (fileType === 'excel') {
            const idx = headers.indexOf(colMap[key]);
            return idx !== -1 ? row[idx] : undefined;
          } else {
            return row[colMap[key]];
          }
        };

        const rawName = getValue('name');
        const nameStr = (rawName ?? '').toString().trim();

        if (!nameStr) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing account name`);
          continue;
        }

        const accountTypeRaw = getValue('type') || 'Asset';
        const validTypes = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];
        const accountType = validTypes.includes(accountTypeRaw) ? accountTypeRaw : 'Asset';

        const accountData = {
          name: nameStr,
          type: accountType,
          userId: user?.uid || '',
          code: (getValue('code') ?? '').toString().trim(),
          financialStatement: (getValue('financialstatement') ?? '').toString().trim(),
          active: getValue('active') !== undefined 
            ? String(getValue('active')).toLowerCase() === 'true' || 
              String(getValue('active')).toLowerCase() === 'yes' || 
              getValue('active') === 1 || getValue('active') === true
            : true,
          createdAt: Timestamp.now()
        };

        const newDocRef = doc(accountsCollectionRef);
        batch.set(newDocRef, accountData);
        successCount++;
      }

      if (successCount > 0) {
        await batch.commit();
      }

      let message = `Imported ${successCount} account${successCount === 1 ? '' : 's'} successfully`;
      if (errorCount > 0) {
        message += ` • ${errorCount} row${errorCount === 1 ? '' : 's'} skipped`;
      }
      setSuccessMessage(message);

      if (errors.length > 0) {
        console.warn("Import issues:\n" + errors.join("\n"));
      }

      setFile(null);
      setIsBulkImportModalOpen(false);

    } catch (error) {
      console.error("Bulk import failed:", error);
      setError(`Bulk import failed: ${error.message || "Unknown error"}`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const generateAccountsPDF = () => {
    if (accounts.length === 0) {
      setError("No accounts available to generate PDF");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let currentY = 10;
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, currentY + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Chart of Hotel Accounts Report", 35, currentY + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    currentY += 28;

    // Sort accounts by code using natural sort
    const sortedAccounts = [...accounts].sort(naturalSort);

    const tableData = sortedAccounts.map((account, index) => [
      (index + 1).toString(),
      account.code || '-',
      account.name,
      account.type,
      account.financialStatement || '-',
      account.active ? 'Active' : 'Inactive',
      account.createdAt ? dateFormatter.format(account.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Code', 'Name', 'Type', 'Financial Statement', 'Status', 'Created Date']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 14, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 40 },
        5: { cellWidth: 20 },
        6: { cellWidth: 25 }
      },
      didDrawPage: (data) => {
        currentY = data.table.finalY || data.cursor.y;
        if (data.pageNumber < doc.internal.getNumberOfPages()) {
          doc.setPage(data.pageNumber + 1);
          doc.addImage(Logo, "PNG", 14, 10, 15, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(hotelName, 35, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "normal");
          doc.text("Chart of Hotel Accounts Report", 35, 23);
          doc.setFontSize(10);
          doc.text(`Generated by: ${fullName}`, 35, 28);
          doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);
          currentY = 38;
        }
      }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`chart_of_hotel_accounts_${formatDateForFilename(new Date())}.pdf`);
  };

  const handleEditAccount = (accountToEdit) => {
    setEditingAccount(accountToEdit);
    setNewAccount({ 
      name: accountToEdit.name,
      type: accountToEdit.type,
      userId: accountToEdit.userId || user?.uid || '',
      code: accountToEdit.code || '',
      financialStatement: accountToEdit.financialStatement || '',
      active: accountToEdit.active
    });
    setIsEditModalOpen(true);
  };

  const confirmDeactivateAccount = (accountToDeactivate) => {
    setCurrentAccount(accountToDeactivate);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateAccount = async (id) => {
    setUpdate(true);
    try {
      const accountDoc = doc(db, "hotelAccounts", id);
      await updateDoc(accountDoc, { active: false });
      setSuccessMessage("Account deactivated successfully");
      // fetchAccounts();
    } catch (error) {
      setError("Failed to deactivate account");
    } finally {
      setUpdate(false);
      setIsDeactivateModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingAccounts && !hasFetchedAccounts) {
      return <p className="loading-text">Loading accounts ...</p>;
    }
    
    if (filteredAccounts.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No accounts match your search.</p>;
      } else {
        return <p className="loading-text">No accounts found. Add your first account or import accounts in bulk.</p>;
      }
    }
    
    return filteredAccounts.map((accountItem) => (
      <div key={accountItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{accountItem.name}</strong><br />
            <span>Type: {accountItem.type}</span>&nbsp;-&nbsp;
            <span>Code: {accountItem.code}</span><br />
            <span>{accountItem.financialStatement || 'Not specified'}</span><br />
            <span className="size5 font-gold bold">{accountItem.active ? 'Active' : 'Not active'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditAccount(accountItem)}>
            <i className="fas fa-edit"></i>
          </button>
          {accountItem.active && (
            <button className="list-btn delete-btn" onClick={() => confirmDeactivateAccount(accountItem)}>
              <i className="fas fa-ban"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Chart of Hotel Accounts</h2>
            <Link to="#" className="home-link" onClick={() => window.location.reload()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
            <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Account</button>    
              <button className="list-btn" onClick={generateAccountsPDF}><i className="fas fa-print"></i></button>          
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>                            
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Account"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Account</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Account Name"
            value={newAccount.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="type" className="input-label">Type</label>
          <select
            name="type"
            className="input-field"
            value={newAccount.type}
            onChange={handleInputChange}
          >
            <option value="Asset">Asset</option>
            <option value="Liability">Liability</option>
            <option value="Equity">Equity</option>
            <option value="Income">Income</option>
            <option value="Expense">Expense</option>
          </select>
          <label htmlFor="code" className="input-label">Code</label>
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Account Code"
            value={newAccount.code}
            onChange={handleInputChange}
          />
          <label htmlFor="financialStatement" className="input-label">Financial Statement</label>
          <select
            name="financialStatement"
            className="input-field"
            value={newAccount.financialStatement}
            onChange={handleInputChange}
          >
            <option value="">Select Financial Statement (Optional)</option>
            <option value="Balance Sheet">Balance Sheet</option>
            <option value="Income Statement (P&L)">Income Statement (P&L)</option>
            <option value="Cash Flow Statement">Cash Flow Statement</option>
            <option value="Statement of Equity">Statement of Equity</option>
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddAccount}>Add Account</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewAccount({ 
            name: '',
            type: 'Asset',
            userId: user?.uid || '',
            code: '',
            financialStatement: '',
            active: true
          });
          setEditingAccount(null);
        }}
        contentLabel="Edit Account"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Account</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Account Name"
            value={newAccount.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="type" className="input-label">Type</label>
          <select
            name="type"
            className="input-field"
            value={newAccount.type}
            onChange={handleInputChange}
          >
            <option value="Asset">Asset</option>
            <option value="Liability">Liability</option>
            <option value="Equity">Equity</option>
            <option value="Income">Income</option>
            <option value="Expense">Expense</option>
          </select>
          <label htmlFor="code" className="input-label">Code</label>
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Account Code"
            value={newAccount.code}
            onChange={handleInputChange}
          />
          <label htmlFor="financialStatement" className="input-label">Financial Statement</label>
          <select
            name="financialStatement"
            className="input-field"
            value={newAccount.financialStatement}
            onChange={handleInputChange}
          >
            <option value="">Select Financial Statement (Optional)</option>
            <option value="Balance Sheet">Balance Sheet</option>
            <option value="Income Statement (P&L)">Income Statement (P&L)</option>
            <option value="Cash Flow Statement">Cash Flow Statement</option>
            <option value="Statement of Equity">Statement of Equity</option>
          </select>
          <label>
            <input
              type="checkbox"
              name="active"
              checked={newAccount.active}
              onChange={(e) => setNewAccount({ ...newAccount, active: e.target.checked })}
            />
            Active
          </label>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              setNewAccount({ 
                name: '',
                type: 'Asset',
                userId: user?.uid || '',
                code: '',
                financialStatement: '',
                active: true
              });
              setEditingAccount(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateAccount}>Update Account</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeactivateModalOpen}
        onRequestClose={() => setIsDeactivateModalOpen(false)}
        contentLabel="Deactivate Account"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Deactivate Account</h2>
        {currentAccount && (
          <div>
            <p>Are you sure you want to deactivate <strong>{currentAccount.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeactivateAccount(currentAccount.id)}>Deactivate</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
        }}
        contentLabel="Bulk Import Accounts"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Accounts</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> name</p>
            <p><strong>OPTIONAL FIELDS:</strong> type, code, financialStatement, active</p>
            <p><strong>VALID TYPES:</strong> Asset, Liability, Equity, Income, Expense (default: Asset)</p>
            <p><strong>ACTIVE FIELD:</strong> true/false (default: true)</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HotelAccounts;